import { marked } from "marked";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import {
  MetaFunction,
  useLoaderData, 
} from "@remix-run/react";
import { renderToString } from 'react-dom/server';
import invariant from "tiny-invariant";
import { Component as OliveOilAd } from "~/components/olive-oil-ad";
import { useLoadScript } from "~/hooks/load-script";
import { outboundLinks } from "~/shared/outbound-links";
import { metaBuilder } from "~/shared/meta-builder";
import { useMemo } from "react";

const embedVideoCodes = [
  'VK6L71m-D3M',
  'WbousVoza2o',
  'aheNoV76bYk',
  'IwfzKxyuyFE',
  'ZxTJcF-qRI8',
  'cif-_KvN7YA',
  'RLEtd7K6I34',
  '0vK5bcjamJ0',
  'eyr7RNu2GDk',
  'U7deVafqsdI',
  'Xpj6tvt28vU',
  '7cyQZq0c4KM',
  'H6Ux2hdzQYg',
  '5lAnhdVGWXI',
  'hQik9rM72Gg',
  'NPR8CD6_qVI',
  'UPavngf7kgQ',
  '_17GdL6-6os',
  'j2EXdy6QeIQ',
  'll4jGGzbRFY',
  'YiJbSEK9ax0',
  '6qOdTWeeUHM',
  'J72AMnX4PLE',
  '8f0C3iGR0Ww',
  'ykhJWSeo6lE',
  'a4CeFGCodYM',
  'oSWoCGbZ3XE',
  'NiVDp8H8g8g'
];

const textReplacement = (
  content: string[],
  regex: RegExp,
  replacement: string,
  stopOnFirst: boolean = false,
) => {
  for (let i = 0; i < content.length; i++) {
    if (regex.test(content[i])) {
      content[i] = content[i].replace(regex, replacement);
      
      if (stopOnFirst)
        break;
    }
  }
};

const insertOliveOilAd = (content: string[]) => {
  const outboundHref = outboundLinks.bigHorn.root + outboundLinks.bigHorn.evoos;
  const oliveOilRegex = /olive oil/i;
  const oliveOilReplacement = `<a class="inline-link" href="${outboundHref}" rel="noopener" onClick="va && va('event', { name: 'bhooc-ad-inline-evoos' });">premium olive oil</a>`;
  textReplacement(content, oliveOilRegex, oliveOilReplacement, true);
};

const insertBalsamicVinegarAd = (content: string[]) => {
  const outboundHref = outboundLinks.bigHorn.root + outboundLinks.bigHorn.balsamics;
  const balsamicRegex = /balsamic vinegar/i;
  const balsamicReplacement = `<a class="inline-link" href="${outboundHref}" rel="noopener" onClick="va && va('event', { name: 'bhooc-ad-inline-balsamics' });">traditional Italian balsamic vinegar</a>`;
  textReplacement(content, balsamicRegex, balsamicReplacement, true);
};

export const loader = async ({ params }: LoaderFunctionArgs) => {
  invariant(params.recipe, "Missing recipe param");

  const response = await fetch(`https://www.bhooc-recipes.com/recipe/${params.recipe}.json`);

  if (response.status !== 200) {
    throw new Response("Not Found", { status: 404 });
  }
  
  const data = await response.json();
  
  const adInsert = renderToString(OliveOilAd());
  const tags = data.categories ? JSON.parse(data.categories.replace(/'/g, '"')) : [];
  const metadata = {
    'title': data.title,
    'description': data.seo_description,
    'pageTitle': data.page_title,
    'cuisine': data.cuisine,
    'tags': tags,
    'imageUrl': null, // TODO
  };
  const content = await Promise.all([
    data.instructions,
    data.who_good_for_description,
  ].map(markdown => marked(markdown)));

  insertOliveOilAd(content);
  insertBalsamicVinegarAd(content);
  
  content.splice(1, 0, adInsert);
  return json({ metadata, content });
};

export const meta: MetaFunction<typeof loader> = ({
  data,
}) => {
  return metaBuilder({
    title: `BH Recipe | ${data?.metadata?.title}`,
    description: data?.metadata?.description,
    url: `/recipe/${data?.metadata?.pageTitle}`,
    imageUrl: data?.metadata?.imageUrl ?? '/logo.png',
    imageAlt: data?.metadata?.title,
  });
};

export default function Recipe() {
  const { metadata, content } = useLoaderData<typeof loader>();
  useLoadScript('/_vercel/insights/script.js');
  const embedVideoCode = useMemo(() => {
    const randomIndex = Math.floor(Math.random() * embedVideoCodes.length);
    return embedVideoCodes[randomIndex];
  }, []);

  return (
    <>
      <div
        className="page-container bg-cover bg-center"
        style={{ backgroundImage: "url('/images/recipe-bg.jpg')" }}
      >
        <div className="recipe-container recipe max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
          {metadata.title && (
            <>
              <h1 className="text-3xl font-bold mb-6 text-gray-700">{metadata.title}</h1>
              <hr />
            </>
          )}
          {metadata.cuisine && (
            <div className="inline-block px-2 py-1 mb-2 font-semibold text-yellow-800">{metadata.cuisine} cuisine</div>
          )}
          <div className="mb-4">
            {metadata.tags.map((tag: string, index: number) => (
              <span
                key={index}
                className="inline-block px-2 py-1 mr-2 mb-2 text-sm font-semibold text-green-700 bg-green-100 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="relative">
            <div className="md:float-right md:ml-4 md:w-[315px] mb-4">
              <iframe 
                className="w-full h-[560px]"
                src={`https://www.youtube.com/embed/${embedVideoCode}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="markdown">
              {content.map((html, index) => (
                <div 
                  key={index} 
                  id={`container-${index}`}
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              ))}
            </div>
          </div>
          <OliveOilAd />
        </div>
      </div>
    </>
  );
}
